import React from 'react';
import { Box, Card, CardContent, Container, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useTranslation } from 'react-i18next';

/* Tanıtım sayfalarının (/tanitim, /emlak-crm, /about) ortak görsel dili.
   Renkler yalnızca tema token'larından türetilir: açık ve koyu modda aynı bileşenler çalışır. */

export const STORE_LINKS = {
  appStore: 'https://apps.apple.com/tr/app/odazen-mobil/id6753760605?l=tr',
  playStore: 'https://play.google.com/store/apps/details?id=com.craftopya.odazen',
};

export const ACCENT = '#4F46E5';

/** t(key, { returnObjects: true }) her zaman dizi döndürsün (eksik anahtar sayfayı çökertmesin). */
export const tList = (t, key) => {
  const value = t(key, { returnObjects: true });
  return Array.isArray(value) ? value : [];
};

export const LandingHero = ({ labelledBy, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  return (
    <Box
      component="section"
      aria-labelledby={labelledBy}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 5, md: 9 },
        pb: { xs: 7, md: 11 },
        background: isDark
          ? `radial-gradient(900px 480px at 88% -8%, ${alpha(primary, 0.26)}, transparent 62%),
             radial-gradient(760px 420px at -12% 112%, ${alpha(ACCENT, 0.2)}, transparent 60%),
             ${theme.palette.background.default}`
          : `radial-gradient(900px 480px at 88% -8%, ${alpha(primary, 0.16)}, transparent 62%),
             radial-gradient(760px 420px at -12% 112%, ${alpha(ACCENT, 0.1)}, transparent 60%),
             linear-gradient(180deg, ${alpha(primary, 0.05)} 0%, ${theme.palette.background.default} 100%)`,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {children}
      </Container>
    </Box>
  );
};

export const Eyebrow = ({ children, sx }) => {
  const theme = useTheme();
  return (
    <Box
      component="p"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        m: 0,
        mb: 2.5,
        px: 1.5,
        py: 0.6,
        borderRadius: 999,
        fontSize: '0.82rem',
        fontWeight: 700,
        color: 'primary.main',
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.09),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        ...sx,
      }}
    >
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
      {children}
    </Box>
  );
};

/**
 * Köşe kurdelesi: kartın sağ üst köşesine yaslanan, sol ucu çentikli şerit ("Ücretsiz" vb.).
 * Gerçek metindir (ekran okuyucu okur); üst öğe `position: relative` + `overflow: hidden` olmalı.
 * Metin rengi zemine göre seçilir (açık tonlarda koyu, koyu tonlarda beyaz metin; ≥4.5:1).
 * İçerikle çakışmaması için kartın üst boşluğu kurdele yüksekliğini (26px + `top`) karşılamalıdır.
 */
export const CornerRibbon = ({ children, color, top = 14, sx }) => {
  const theme = useTheme();
  const bg = color || theme.palette.success.main;
  return (
    <Box
      component="span"
      data-ribbon=""
      sx={{
        position: 'absolute',
        top,
        right: 0,
        zIndex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 26,
        pl: '15px',
        pr: 1.25,
        bgcolor: bg,
        color: theme.palette.getContrastText(bg),
        fontSize: '0.75rem',
        fontWeight: 800,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 9px 50%)',
        pointerEvents: 'none',
        ...sx,
      }}
    >
      <LocalOfferOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
      {children}
    </Box>
  );
};

/** Hero'daki "ücretsiz" rozeti (gerçek metin). `href` verilirse sayfa içi bağlantı olur (ör. #paketler). */
export const FreeBadge = ({ children, href, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const color = isDark ? '#6EE7B7' : '#047857';
  return (
    <Box
      component={href ? 'a' : 'p'}
      href={href}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        m: 0,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        fontSize: '0.92rem',
        fontWeight: 700,
        lineHeight: 1.35,
        color,
        textDecoration: 'none',
        bgcolor: alpha(color, isDark ? 0.14 : 0.1),
        border: `1px solid ${alpha(color, isDark ? 0.4 : 0.3)}`,
        ...(href && {
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: alpha(color, isDark ? 0.22 : 0.16) },
          '&:focus-visible': { outline: `3px solid ${alpha(color, 0.55)}`, outlineOffset: 2 },
        }),
        ...sx,
      }}
    >
      <LocalOfferOutlinedIcon aria-hidden sx={{ fontSize: 18 }} />
      {children}
      {href ? <ArrowForwardRoundedIcon aria-hidden sx={{ fontSize: 16 }} /> : null}
    </Box>
  );
};

export const HeroTitle = ({ id, children }) => (
  <Typography
    id={id}
    component="h1"
    sx={{
      fontWeight: 800,
      letterSpacing: '-0.03em',
      lineHeight: 1.1,
      fontSize: { xs: '2.1rem', sm: '2.6rem', md: '3.25rem' },
    }}
  >
    {children}
  </Typography>
);

export const HeroSubtitle = ({ children, sx }) => (
  <Typography
    sx={{
      mt: 2.5,
      color: 'text.secondary',
      fontSize: { xs: '1.02rem', md: '1.15rem' },
      lineHeight: 1.7,
      maxWidth: 600,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/** Hero altındaki kısa maddeler (yan yana, mobilde alt alta kayar). */
export const InlinePoints = ({ items }) => (
  <Box
    component="ul"
    sx={{ listStyle: 'none', p: 0, m: 0, mt: 3.5, display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1 }}
  >
    {items.map((item) => (
      <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', fontSize: '0.92rem', fontWeight: 500 }}>
        <CheckCircleRoundedIcon aria-hidden sx={{ fontSize: 18, color: 'success.main' }} />
        {item}
      </Box>
    ))}
  </Box>
);

export const LandingSection = ({ id, tone = 'default', labelledBy, children, sx, maxWidth = 'lg' }) => {
  const theme = useTheme();
  const bgcolor =
    tone === 'muted'
      ? theme.palette.background.paper
      : tone === 'tinted'
        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04)
        : 'transparent';
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={labelledBy}
      sx={{
        py: { xs: 7, md: 10 },
        bgcolor,
        scrollMarginTop: { xs: 70, md: 80 },
        ...(tone === 'muted' && {
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        ...sx,
      }}
    >
      <Container maxWidth={maxWidth}>{children}</Container>
    </Box>
  );
};

export const SectionHeading = ({ id, eyebrow, title, subtitle, align = 'center', component = 'h2', sx }) => (
  <Box
    sx={{
      textAlign: align,
      maxWidth: align === 'center' ? 760 : 'none',
      mx: align === 'center' ? 'auto' : 0,
      mb: { xs: 4, md: 6 },
      ...sx,
    }}
  >
    {eyebrow && (
      <Typography
        component="p"
        sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.25 }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography
      id={id}
      component={component}
      sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, fontSize: { xs: '1.7rem', md: '2.35rem' } }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ mt: 1.5, color: 'text.secondary', fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.7 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const IconTile = ({ children, size = 48, color = 'primary', radius = '14px' }) => {
  const theme = useTheme();
  const main = theme.palette[color]?.main || theme.palette.primary.main;
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: main,
        bgcolor: alpha(main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
      }}
    >
      {children}
    </Box>
  );
};

export const FeatureCard = ({ icon, title, description, headingComponent = 'h3', iconColor, compact = false }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: '100%',
        bgcolor: 'background.paper',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: `0 14px 32px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1)}`,
        },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <CardContent sx={{ p: compact ? 2.5 : 3, '&:last-child': { pb: compact ? 2.5 : 3 } }}>
        {icon && <IconTile size={compact ? 42 : 48} color={iconColor}>{icon}</IconTile>}
        <Typography component={headingComponent} sx={{ mt: icon ? 2 : 0, fontWeight: 700, fontSize: compact ? '1rem' : '1.08rem', lineHeight: 1.35 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: compact ? '0.92rem' : '0.95rem', lineHeight: 1.6 }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

/** Numaralı adımlar (ol/li) — mobilde alt alta, masaüstünde yan yana. */
export const StepList = ({ steps, headingComponent = 'h3' }) => {
  const theme = useTheme();
  return (
    <Box
      component="ol"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'grid',
        gap: { xs: 2, md: 3 },
        gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))` },
        counterReset: 'step',
      }}
    >
      {steps.map((step, index) => (
        <Box
          component="li"
          key={step.title}
          sx={{
            position: 'relative',
            p: { xs: 2.5, md: 3 },
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            gap: 2,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
              color: 'primary.contrastText',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${ACCENT})`,
              boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            {index + 1}
          </Box>
          <Box>
            <Typography component={headingComponent} sx={{ fontWeight: 700, fontSize: '1.08rem' }}>
              {step.title}
            </Typography>
            <Typography sx={{ mt: 0.75, color: 'text.secondary', lineHeight: 1.65, fontSize: '0.95rem' }}>
              {step.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const CheckList = ({ items, icon, iconColor = 'success.main', dense = false }) => (
  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: dense ? 1 : 1.5 }}>
    {items.map((item) => (
      <Box component="li" key={item} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <Box aria-hidden sx={{ color: iconColor, display: 'flex', mt: '2px' }}>
          {icon || <CheckCircleRoundedIcon sx={{ fontSize: 20 }} />}
        </Box>
        <Typography sx={{ fontSize: dense ? '0.93rem' : '0.98rem', lineHeight: 1.55 }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

const StoreBadge = ({ href, icon, small, big, ariaLabel }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1,
        minWidth: 164,
        borderRadius: '12px',
        bgcolor: '#0B0F19',
        color: '#fff',
        textDecoration: 'none',
        border: `1px solid ${isDark ? alpha('#fff', 0.22) : '#0B0F19'}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 22px rgba(0,0,0,0.25)' },
        '&:focus-visible': { outline: `3px solid ${alpha(theme.palette.primary.main, 0.6)}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 24, width: 24 }} aria-hidden="true" />
      <Box component="span" sx={{ textAlign: 'left', lineHeight: 1.15 }}>
        <Box component="span" sx={{ display: 'block', fontSize: 11, opacity: 0.85 }}>{small}</Box>
        <Box component="span" sx={{ display: 'block', fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>{big}</Box>
      </Box>
    </Box>
  );
};

export const StoreBadges = ({ justify = 'flex-start', sx }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: justify, ...sx }}>
      <StoreBadge
        href={STORE_LINKS.appStore}
        icon={faApple}
        small={t('landing.common.store.appStoreSmall')}
        big={t('landing.common.store.appStore')}
        ariaLabel={t('landing.common.store.appStoreAria')}
      />
      <StoreBadge
        href={STORE_LINKS.playStore}
        icon={faGooglePlay}
        small={t('landing.common.store.playStoreSmall')}
        big={t('landing.common.store.playStore')}
        ariaLabel={t('landing.common.store.playStoreAria')}
      />
    </Box>
  );
};

/** Basit telefon çerçevesi (dekoratif — ekran okuyuculardan gizli). */
export const PhoneMock = ({ children, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      aria-hidden
      sx={{
        width: { xs: 240, sm: 260 },
        mx: 'auto',
        p: 1.25,
        borderRadius: '38px',
        bgcolor: isDark ? '#1F2937' : '#0F172A',
        boxShadow: isDark ? '0 30px 70px rgba(0,0,0,0.55)' : '0 30px 70px rgba(15,23,42,0.25)',
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '30px',
          overflow: 'hidden',
          bgcolor: theme.palette.background.default,
          minHeight: 440,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mx: 'auto', mt: 1, mb: 0.5, width: 90, height: 22, borderRadius: 999, bgcolor: isDark ? '#1F2937' : '#0F172A' }} />
        {children}
      </Box>
    </Box>
  );
};

/** Skeleton benzeri çizgi (önizlemelerde metin yerine). */
export const MockLine = ({ width = '100%', height = 7, strength = 1, sx }) => {
  const theme = useTheme();
  const base = theme.palette.mode === 'dark' ? 0.2 : 0.12;
  return <Box sx={{ height, width, borderRadius: 999, bgcolor: alpha(theme.palette.text.primary, base * strength), ...sx }} />;
};

export const ctaWhiteButtonSx = {
  bgcolor: '#fff',
  color: '#1D4ED8',
  '&:hover': { bgcolor: alpha('#fff', 0.9), boxShadow: 'none' },
};

export const ctaGhostButtonSx = {
  color: '#fff',
  borderColor: alpha('#fff', 0.6),
  '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.12) },
};

/** Sayfa sonu çağrı bandı: koyu/açık modda aynı mavi degrade (beyaz metin kontrastı sabit). */
export const CtaBanner = ({ headingId, title, description, children, footer }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: { xs: 4, md: 6 },
      px: { xs: 3, md: 8 },
      py: { xs: 5, md: 7 },
      textAlign: 'center',
      color: '#fff',
      background: `linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, ${ACCENT} 100%)`,
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(600px 260px at 85% 0%, rgba(255,255,255,0.18), transparent 60%)',
        pointerEvents: 'none',
      },
    }}
  >
    <Box sx={{ position: 'relative' }}>
      <Typography id={headingId} component="h2" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '1.6rem', md: '2.2rem' }, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ mt: 1.5, opacity: 0.92, maxWidth: 640, mx: 'auto', fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.65 }}>
          {description}
        </Typography>
      )}
      <Box sx={{ mt: 3.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>{children}</Box>
      {footer && <Box sx={{ mt: 2.5 }}>{footer}</Box>}
    </Box>
  </Box>
);
